import { useState } from "react";
import { CopyPlus, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";

// Re-sends only the skipped invoices with override rules; returns the merged result.
export async function overrideSkips(invoices, result, modes) {
  const picked = result.skipped.filter((s) => (modes.on_number !== "skip" && s.kind === "number") || (modes.on_date !== "skip" && s.kind === "date"));
  const { data } = await api.post("/invoices/batch", { invoices: picked.map((s) => invoices[s.index]), batch_id: result.batch_id, ...modes });
  const left = result.skipped.filter((s) => !picked.includes(s));
  const again = data.skipped.map((s) => ({ ...s, index: picked[s.index].index }));
  return { ...result, created: [...result.created, ...data.created], skipped: [...left, ...again], changed: data.created.length };
}

export default function SkipOverride({ result, invoices, onDone }) {
  const [busy, setBusy] = useState("");
  const nums = result.skipped.filter((s) => s.kind === "number").length;
  const dates = result.skipped.filter((s) => s.kind === "date").length;
  if (!nums && !dates) return null;

  const go = (key, modes, ask) => async () => {
    if (ask && !window.confirm(ask)) return;
    setBusy(key);
    try {
      const merged = await overrideSkips(invoices, result, { on_number: "skip", on_date: "skip", ...modes });
      await onDone(merged);
      toast.success(`${merged.changed} skipped invoice${merged.changed === 1 ? "" : "s"} overridden`);
    } catch (e) { toast.error(formatError(e)); }
    setBusy("");
  };
  const icon = (k, I) => (busy === k ? <Loader2 size={14} className="animate-spin" /> : <I size={14} />);

  return (
    <div className="flex flex-wrap items-center gap-2 border border-amber-400/30 bg-amber-400/5 rounded-md p-2.5" data-testid="skip-override">
      <span className="text-xs text-amber-300 mr-1">Override skips:</span>
      {nums > 0 && (
        <Button size="sm" variant="outline" disabled={!!busy} data-testid="override-replace-number"
          onClick={go("num", { on_number: "replace" }, `Replace ${nums} old invoice${nums === 1 ? " that uses" : "s that use"} the same number with these new details? This can't be undone.`)}>
          {icon("num", RefreshCw)} Replace {nums} old invoice{nums === 1 ? "" : "s"} (same number)
        </Button>
      )}
      {dates > 0 && <>
        <Button size="sm" variant="outline" disabled={!!busy} onClick={go("create", { on_date: "create" })} data-testid="override-date-create">
          {icon("create", CopyPlus)} Create {dates} anyway (new numbers)
        </Button>
        <Button size="sm" variant="outline" disabled={!!busy} data-testid="override-date-replace"
          onClick={go("dreplace", { on_date: "replace" }, `Replace the existing invoice for that date on ${dates} customer${dates === 1 ? "" : "s"}? This can't be undone.`)}>
          {icon("dreplace", RefreshCw)} Replace {dates} existing for that date
        </Button>
      </>}
    </div>
  );
}
